import { useAuthStore } from '@/stores/auth-store';
import type { CompanyInfo } from '@scio3/shared-types';

/**
 * Pure feature flag extraction — testable without React.
 * Returns boolean feature flags with safe defaults.
 */
export function getCompanyFeatures(company: CompanyInfo | null) {
  const feature = company?.feature;
  const featureData = company?.featureData;

  return {
    isRemoteCommand: featureData?.isRemoteCommand ?? false,
    allowTeams: feature?.allowTeams ?? false,
    samlSSO: feature?.samlSSO ?? false,
    proofOfPlay: feature?.proofOfPlay ?? false,
    proofOfPlayStatus: featureData?.proofOfPlayStatus ?? false,
    ai: feature?.ai ?? false,
    engage: feature?.engage ?? false,
    apiKey: feature?.apiKey ?? false,
    apiAccessToken: feature?.apiAccessToken ?? false,
    branding: feature?.branding ?? false,
    approvalWorkflow: feature?.approvalWorkflow ?? false,
    customBrandCanva: feature?.customBrandCanva ?? false,
    folderLevelSecurity: feature?.folderLevelSecurity ?? false,
    advancedDeviceManagement: feature?.advancedDeviceManagement ?? false,
    advancedSchedule: feature?.advancedSchedule ?? false,
    cecSchedule: feature?.cecSchedule ?? false,
    remoteControl: feature?.remoteControl ?? false,
    remoteCommand: feature?.remoteCommand ?? false,
    lockdownDevice: feature?.lockdownDevice ?? false,
    sensor: feature?.sensor ?? false,
    roleSecurity: feature?.roleSecurity ?? false,
    securityAuditLogging: feature?.securityAuditLogging ?? false,
    alertPolicy: feature?.alertPolicy ?? false,
    externalCom: feature?.externalCom ?? false,
    comLog: feature?.comLog ?? false,
    shareLink: feature?.shareLink ?? false,
    totpMfa: feature?.totpMfa ?? false,
    phoneMfa: feature?.phoneMfa ?? false,
    contentPlaylistTag: feature?.contentPlaylistTag ?? false,
    subPlaylistControl: feature?.subPlaylistControl ?? false,
    playEvery: feature?.playEvery ?? false,
    resumeOnNextPlay: feature?.resumeOnNextPlay ?? false,
    feedsCreate: feature?.feedsCreate ?? false,
    feedsManage: feature?.feedsManage ?? false,
    feedsCategories: feature?.feedsCategories ?? false,
    enterpriseAPP: feature?.enterpriseAPP ?? false,
    // Enterprise / billing-managed accounts can opt out of the unpaid-invoice
    // gate so the wizard / payment flows don't block them. SCIO 1.0 parity:
    // subscriptionPlanModal.js:1956.
    disablePaymentCheck: feature?.disablePaymentCheck ?? false,
    enforcePasswordComplexity: feature?.enforcePasswordComplexity ?? false,
    utilsMDM: feature?.utilsMDM ?? false,
    aericast: feature?.aericast ?? false,
    apiGateway: feature?.apiGateway ?? false,
    dynamicDataMapping: feature?.dynamicDataMapping ?? false,
    isSecurityAuditLogging: featureData?.isSecurityAuditLogging ?? false,
    // Infrastructure / Data Residency
    disableS3: feature?.disableS3 ?? false,
    disableDOSpaces: feature?.disableDOSpaces ?? false,
    powerbiServicePrincipal: feature?.powerbiServicePrincipal ?? false,
    allowPowerBI: feature?.allowPowerBI ?? false,
    allowLooker: feature?.allowLooker ?? false,
    secureLink: feature?.secureLink ?? false,
    teamBilling: feature?.teamBilling ?? false,
    scim: feature?.scim ?? false,
    smallappManifest: feature?.smallappManifest ?? false,
    // Order-device addons: gates "OptiSigns Managed Device" (requiredPlan-gated addon)
    // from the device selector when false (SCIO 1.0 orderDeviceComponent.html:148).
    orderManagedDevice: feature?.orderManagedDevice ?? false,
    // International ordering enabled: when true, suppress the non-US shipping
    // warning dialog on Order Device. SCIO 1.0 orderDeviceComponent.js:1112,1130
    // gates `showWarningOutsideUSModal` on `!myCompany.feature.allowIntlOrder`.
    allowIntlOrder: feature?.allowIntlOrder ?? false,
  };
}

/**
 * React hook wrapper for getCompanyFeatures.
 * Reads company from auth store and delegates to pure function.
 * `loaded` is false while company data is still being fetched — use it
 * to avoid flashing plan-gate UIs before the feature flags are known.
 */
export function useCompanyFeatures() {
  const company = useAuthStore((s) => s.company);
  const companyLoaded = useAuthStore((s) => s.companyLoaded);
  return { ...getCompanyFeatures(company), loaded: companyLoaded };
}
